import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import styles from "./Input.module.css";

type FieldWrapProps = {
  label: string;
  required?: boolean;
  hint?: ReactNode;
  error?: string | null;
  htmlFor: string;
  children: ReactNode;
};

function FieldWrap({ label, required, hint, error, htmlFor, children }: FieldWrapProps) {
  return (
    <label className={styles.field} htmlFor={htmlFor}>
      <span className={`${styles.label} ${required ? styles.required : ""}`}>{label}</span>
      {children}
      {error ? <p className={styles.error}>{error}</p> : hint ? <p className={styles.hint}>{hint}</p> : null}
    </label>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: ReactNode;
  error?: string | null;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, id, ...rest },
  ref,
) {
  const internalId = useId();
  const inputId = id ?? internalId;
  return (
    <FieldWrap label={label} required={required} hint={hint} error={error} htmlFor={inputId}>
      <input
        id={inputId}
        ref={ref}
        required={required}
        aria-invalid={!!error || undefined}
        className={`${styles.input} ${error ? styles.invalid : ""}`}
        {...rest}
      />
    </FieldWrap>
  );
});

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  hint?: ReactNode;
  error?: string | null;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, id, ...rest },
  ref,
) {
  const internalId = useId();
  const inputId = id ?? internalId;
  return (
    <FieldWrap label={label} required={required} hint={hint} error={error} htmlFor={inputId}>
      <textarea
        id={inputId}
        ref={ref}
        required={required}
        aria-invalid={!!error || undefined}
        className={`${styles.textarea} ${error ? styles.invalid : ""}`}
        {...rest}
      />
    </FieldWrap>
  );
});

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  hint?: ReactNode;
  error?: string | null;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, id, children, ...rest },
  ref,
) {
  const internalId = useId();
  const inputId = id ?? internalId;
  return (
    <FieldWrap label={label} required={required} hint={hint} error={error} htmlFor={inputId}>
      <select
        id={inputId}
        ref={ref}
        required={required}
        aria-invalid={!!error || undefined}
        className={`${styles.select} ${error ? styles.invalid : ""}`}
        {...rest}
      >
        {children}
      </select>
    </FieldWrap>
  );
});
